/**
 * Responsibility: show the cards that did not make the shortlist, with the
 * reason for each.
 *
 * Deliberately not hidden. A card the operator added and then cannot find
 * anywhere looks like the feature lost it, and the reasons are useful in their
 * own right: "graded copies only fetch 1.4x raw" is a real answer to "should I
 * grade this", and it is the answer for most cards.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ToolbarIconButton } from '@kit/components/buttons';
import type { RejectedCandidate } from '../../api/client';
import { money } from './money';
import { VerifyLinks } from './VerifyLinks';

type RejectedListProps = {
  rows: RejectedCandidate[];
  busy?: boolean;
  onRefresh: (row: RejectedCandidate) => void;
  onRemove: (row: RejectedCandidate) => void;
};

export function RejectedList({
  rows,
  busy = false,
  onRefresh,
  onRemove,
}: RejectedListProps) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing here. Every card on the list clears the costs.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5} divider={<span />} sx={{ minWidth: 0 }}>
      {rows.map((row) => (
        <Stack
          key={row.id}
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.card_name}
              </Typography>
              {row.set_name ? (
                <Chip size="small" variant="outlined" label={row.set_name} />
              ) : null}
              {row.raw_price || row.psa10_price ? (
                <Typography variant="caption" color="text.secondary">
                  raw {money(row.raw_price?.amount)} · PSA 10 {money(row.psa10_price?.amount)}
                </Typography>
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {row.reason}
            </Typography>
            {/* Most useful precisely here: when a card was passed over for
                want of prices, the first question is what the search
                actually returned. */}
            <VerifyLinks card={row} variant="row" />
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <ToolbarIconButton
              aria-label="Re-price from eBay"
              onClick={() => onRefresh(row)}
              disabled={busy}
            >
              <RefreshIcon fontSize="small" />
            </ToolbarIconButton>
            <ToolbarIconButton
              aria-label="Remove from the list"
              onClick={() => onRemove(row)}
              disabled={busy}
            >
              <DeleteIcon fontSize="small" />
            </ToolbarIconButton>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
